import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  SparklesIcon,
  LinkIcon,
  GlobeAltIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  BoltIcon,
  ShieldExclamationIcon,
  ArrowRightIcon,
  ClockIcon,
  CursorArrowRaysIcon
} from '@heroicons/react/24/outline';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';
import { useDomain } from '../contexts/DomainContext';

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

const api = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: 'Bearer ' + token } : {}),
      ...(options.headers || {})
    }
  });
  if (!res.ok) throw new Error('HTTP ' + res.status);
  return res.json();
};

const safe = async (fn, fallback = null) => {
  try { return await fn(); } catch (e) { return fallback; }
};

const cleanDomain = (d) => (d || '')
  .replace(/^https?:\/\//, '')
  .replace(/^www\./, '')
  .replace(/\/$/, '');

const num = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '--';
  const v = Number(n);
  if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
  if (v >= 1000) return (v / 1000).toFixed(1) + 'K';
  return String(Math.round(v * 10) / 10);
};

const scoreTone = (s) => {
  if (s === null || s === undefined) return 'slate';
  if (s >= 80) return 'emerald';
  if (s >= 60) return 'amber';
  return 'rose';
};

const TONE = {
  emerald: { text: 'text-emerald-600', bg: 'bg-emerald-50', ring: 'ring-emerald-200', bar: 'bg-emerald-500', stroke: '#059669' },
  amber:   { text: 'text-amber-600',   bg: 'bg-amber-50',   ring: 'ring-amber-200',   bar: 'bg-amber-500',   stroke: '#d97706' },
  rose:    { text: 'text-rose-600',    bg: 'bg-rose-50',    ring: 'ring-rose-200',    bar: 'bg-rose-500',    stroke: '#e11d48' },
  slate:   { text: 'text-slate-500',   bg: 'bg-slate-50',   ring: 'ring-slate-200',   bar: 'bg-slate-400',   stroke: '#64748b' },
  indigo:  { text: 'text-indigo-600',  bg: 'bg-indigo-50',  ring: 'ring-indigo-200',  bar: 'bg-indigo-500',  stroke: '#4f46e5' }
};

/* ------------------------------------------------------------------ */
/* primitives                                                          */
/* ------------------------------------------------------------------ */

const Card = ({ title, subtitle, action, children, className = '' }) => (
  <div className={'bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col ' + className}>
    {(title || action) && (
      <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-slate-100">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-800 truncate">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5 truncate">{subtitle}</p>}
        </div>
        {action}
      </div>
    )}
    <div className="p-5 flex-1">{children}</div>
  </div>
);

const Empty = ({ icon: Icon, title, body, cta, to }) => (
  <div className="h-full flex flex-col items-center justify-center text-center py-6">
    <div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center mb-3">
      <Icon className="w-5 h-5 text-slate-400" />
    </div>
    <p className="text-sm font-medium text-slate-700">{title}</p>
    <p className="text-xs text-slate-500 mt-1 max-w-xs">{body}</p>
    {cta && to && (
      <Link
        to={to}
        className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
      >
        {cta} <ArrowRightIcon className="w-3.5 h-3.5" />
      </Link>
    )}
  </div>
);

const Kpi = ({ label, value, unit, delta, tone = 'slate', icon: Icon, to, hint }) => {
  const t = TONE[tone] || TONE.slate;
  const inner = (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 h-full hover:border-indigo-300 transition-colors">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</span>
        {Icon && <Icon className={'w-4 h-4 ' + t.text} />}
      </div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className={'text-2xl font-bold ' + (value === '--' ? 'text-slate-300' : 'text-slate-900')}>{value}</span>
        {unit && value !== '--' && <span className="text-xs text-slate-400">{unit}</span>}
      </div>
      {delta !== null && delta !== undefined && delta !== 0 ? (
        <div className={'mt-1 flex items-center gap-1 text-xs font-medium ' + (delta > 0 ? 'text-emerald-600' : 'text-rose-600')}>
          {delta > 0 ? <ArrowTrendingUpIcon className="w-3.5 h-3.5" /> : <ArrowTrendingDownIcon className="w-3.5 h-3.5" />}
          {Math.abs(delta)} {hint || 'vs last check'}
        </div>
      ) : (
        <div className="mt-1 text-xs text-slate-400">{hint || ' '}</div>
      )}
    </div>
  );
  return to ? <Link to={to} className="block h-full">{inner}</Link> : inner;
};

const Gauge = ({ value, label }) => {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const tone = TONE[scoreTone(value)];
  const r = 52;
  const c = 2 * Math.PI * r;
  return (
    <div className="flex flex-col items-center">
      <svg width="132" height="132" viewBox="0 0 132 132">
        <circle cx="66" cy="66" r={r} fill="none" stroke="#e2e8f0" strokeWidth="12" />
        <circle
          cx="66" cy="66" r={r} fill="none" stroke={tone.stroke} strokeWidth="12"
          strokeLinecap="round" strokeDasharray={c}
          strokeDashoffset={c - (c * v) / 100}
          transform="rotate(-90 66 66)"
        />
        <text x="66" y="62" textAnchor="middle" className="fill-slate-900" style={{ fontSize: 26, fontWeight: 700 }}>
          {value === null || value === undefined ? '--' : Math.round(v)}
        </text>
        <text x="66" y="80" textAnchor="middle" className="fill-slate-400" style={{ fontSize: 11 }}>
          {label}
        </text>
      </svg>
    </div>
  );
};

const Bar = ({ label, value, max, tone = 'indigo', right }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="mb-2.5 last:mb-0">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-600 capitalize truncate">{label}</span>
        <span className="text-slate-500 font-medium tabular-nums">{right !== undefined ? right : value}</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={'h-full rounded-full ' + (TONE[tone] || TONE.indigo).bar} style={{ width: pct + '%' }} />
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* main                                                                */
/* ------------------------------------------------------------------ */

const OverviewDashboard = () => {
  const domainCtx = useDomain() || {};
  const ctxDomain = domainCtx.selectedDomain;
  const ctxDomains = domainCtx.domains;

  const [domain, setDomain] = useState(
    cleanDomain(ctxDomain || localStorage.getItem('selectedDomain') || '')
  );
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const [audit, setAudit] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [stats, setStats] = useState(null);
  const [google, setGoogle] = useState({ account: null, gsc: null, ga4: null, error: false });
  useEffect(() => { document.title = 'Dashboard | SERPfinity'; }, []);
  const [aiData, setAiData] = useState(null);
  const [backlinks, setBacklinks] = useState(null);

  /* ---- domain list ---- */
  useEffect(() => {
    let live = true;
    (async () => {
      const res = await safe(() => api('/api/domains'), null);
      const list = (res && res.domains ? res.domains : []).map((d) => cleanDomain(d.domain || d.name));
      const ctxList = (ctxDomains || []).map((d) => cleanDomain(typeof d === 'string' ? d : d.domain));
      const merged = Array.from(new Set([...list, ...ctxList].filter(Boolean)));
      if (!live) return;
      setDomains(merged);
      setDomain((cur) => cur || merged[0] || '');
    })();
    return () => { live = false; };
  }, [ctxDomains]);

  /* ---- main load ---- */
  const load = useCallback(async (d) => {
    if (!d) { setLoading(false); return; }
    setRefreshing(true);

    const [auditRes, rankRes, statsRes, acctRes, aiRes, blRes] = await Promise.all([
      safe(() => api('/api/data-for-seo/site-audit/saved/' + encodeURIComponent(d))),
      safe(() => api('/api/rank-tracking/')),
      safe(() => api('/api/user/dashboard-stats')),
      safe(() => api('/api/integrations/accounts')),
      safe(() => api('/api/analysis/saved-report/' + encodeURIComponent(d) + '/visibility-snapshot')),
      safe(() => api('/api/data-for-seo/backlink-analysis/saved/' + encodeURIComponent(d)))
    ]);

    setAudit(auditRes && auditRes.found ? auditRes : null);
    setStats(statsRes ? statsRes.stats : null);
    setAiData((() => {
      const d = aiRes && aiRes.found && aiRes.data ? aiRes.data : null;
      if (!d) return null;
      const av = d.aiVisibility || {};
      const pm = d.platformMeasurement || {};
      const measuredEngines = Object.values(pm).filter((v) => v === true || (v && typeof v === 'object' && (v.measured === true || v.status === 'measured'))).length;
      return {
        visibilityScore: av.measured === false ? null : (typeof av.score === 'number' ? av.score : (typeof d.overallScore === 'number' ? d.overallScore : null)),
        mentions: typeof av.mentions === 'number' ? av.mentions : null,
        citations: Array.isArray(av.topCitedSources) ? av.topCitedSources.length : null,
        enginesCovered: measuredEngines || null,
        measuredAt: aiRes.lastRefreshedAt || d.timestamp || null
      };
    })());
    setBacklinks((() => {
      const d = blRes && blRes.found && blRes.data ? blRes.data : null;
      if (!d) return null;
      const o = d.overview || {};
      const num = (v) => (typeof v === 'number' ? v : null);
      return {
        referringDomains: num(o.referringDomains),
        totalBacklinks: num(o.totalBacklinks),
        domainRank: num(o.domainRank),
        spamScore: num(o.spamScore),
        brokenBacklinks: num(o.brokenBacklinks),
        measuredAt: blRes.lastRefreshedAt || null
      };
    })());

    const all = (rankRes && rankRes.keywords) || [];
    const forDomain = all.filter((k) => cleanDomain(k.domain || k.url) === d);
    setKeywords(forDomain);

    const acct = acctRes && acctRes.accounts ? acctRes.accounts.find((a) => a.provider === 'google') : null;
    if (acct) {
      const sum = await safe(
        () => api('/api/integrations/google/dashboard-summary/' + acct.id, {
          method: 'POST',
          body: JSON.stringify({ days: 28, siteUrl: 'https://' + d })
        }),
        null
      );
      setGoogle({
        account: acct,
        gsc: sum ? sum.gsc : null,
        ga4: sum ? sum.ga4 : null,
        error: !sum || (!sum.gsc && !sum.ga4)
      });
    } else {
      setGoogle({ account: null, gsc: null, ga4: null, error: false });
    }

    setUpdatedAt(new Date());
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { load(domain); }, [domain, load]);

  /* ---- derived ---- */
  const rank = useMemo(() => {
    const withPos = keywords.filter((k) => k.currentPosition > 0);
    const buckets = { top3: 0, four10: 0, eleven20: 0, twentyone50: 0, beyond: 0 };
    let sum = 0, up = 0, down = 0;
    withPos.forEach((k) => {
      const p = k.currentPosition;
      sum += p;
      if (p <= 3) buckets.top3++;
      else if (p <= 10) buckets.four10++;
      else if (p <= 20) buckets.eleven20++;
      else if (p <= 50) buckets.twentyone50++;
      else buckets.beyond++;
      if (k.change > 0) up++;
      if (k.change < 0) down++;
    });
    const movers = [...keywords]
      .filter((k) => k.change)
      .sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
      .slice(0, 5);
    const top = [...withPos].sort((a, b) => a.currentPosition - b.currentPosition).slice(0, 8);
    const aiOverview = keywords.filter(
      (k) => Array.isArray(k.serpFeatures) && k.serpFeatures.includes('ai_overview')
    ).length;
    return {
      total: keywords.length,
      tracked: withPos.length,
      avg: withPos.length ? Math.round((sum / withPos.length) * 10) / 10 : null,
      buckets, up, down, movers, top, aiOverview
    };
  }, [keywords]);

  const trend = useMemo(() => {
    const byDate = {};
    keywords.forEach((k) => {
      (k.history || []).forEach((h) => {
        const day = String(h.date || h.checkedAt || h.createdAt || '').slice(0, 10);
        const pos = Number(h.position);
        if (!day || !pos || pos <= 0) return;
        if (!byDate[day]) byDate[day] = { sum: 0, n: 0 };
        byDate[day].sum += pos;
        byDate[day].n += 1;
      });
    });
    return Object.keys(byDate).sort().slice(-30).map((day) => ({
      date: day.slice(5),
      position: Math.round((byDate[day].sum / byDate[day].n) * 10) / 10
    }));
  }, [keywords]);

  const auditData = audit && audit.data ? audit.data : null;
  const health = auditData ? auditData.healthScore : (audit ? audit.overallScore : null);

  /* ---- action center: what to do next ---- */
  const actions = useMemo(() => {
    const out = [];
    if (auditData && auditData.summary && auditData.summary.errors > 0) {
      out.push({
        tone: 'rose',
        icon: ExclamationTriangleIcon,
        title: auditData.summary.errors + ' technical errors are blocking rankings',
        body: 'Crawl errors and broken elements found in the latest site audit.',
        cta: 'Open Site Audit',
        to: '/site-audit'
      });
    }
    if (rank.down > 0) {
      out.push({
        tone: 'amber',
        icon: ArrowTrendingDownIcon,
        title: rank.down + ' keyword' + (rank.down > 1 ? 's' : '') + ' lost position',
        body: 'Review the pages behind these terms before the drop compounds.',
        cta: 'Open Rank Tracking',
        to: '/rank-tracking'
      });
    }
    if (google.error || !google.account) {
      out.push({
        tone: 'indigo',
        icon: ChartBarIcon,
        title: google.account ? 'Google connection needs re-authorising' : 'Connect Google Search Console',
        body: 'Organic traffic, clicks and impressions stay empty until Google data flows in.',
        cta: google.account ? 'Fix connection' : 'Connect Google',
        to: '/integrations'
      });
    }
    if (!aiData) {
      out.push({
        tone: 'indigo',
        icon: SparklesIcon,
        title: 'No AI visibility scan for this brand yet',
        body: 'See whether ChatGPT, Gemini and Perplexity mention you for your key prompts.',
        cta: 'Run AI Search Visibility',
        to: '/ai-search-visibility'
      });
    }
    if (rank.total === 0) {
      out.push({
        tone: 'indigo',
        icon: MagnifyingGlassIcon,
        title: 'No keywords tracked for this domain',
        body: 'Add your money keywords to start seeing movement week over week.',
        cta: 'Add keywords',
        to: '/rank-tracking'
      });
    }
    return out.slice(0, 4);
  }, [auditData, rank, google, aiData]);

  const cwv = auditData && auditData.coreWebVitals ? auditData.coreWebVitals : null;
  const categories = auditData && auditData.categories ? auditData.categories : null;

  /* ---- render ---- */
  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-8 w-56 bg-slate-200 rounded animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-24 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-64 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 bg-slate-50 min-h-screen">

      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Overview</h1>
          <p className="text-sm text-slate-500">
            Everything that matters for {domain || 'your brand'}, on one screen.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {domains.length === 0 && <option value="">No domains yet</option>}
            {domains.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <button
            onClick={() => load(domain)}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 text-sm font-medium border border-slate-300 bg-white rounded-lg px-3 py-2 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={'w-4 h-4 ' + (refreshing ? 'animate-spin' : '')} />
            Refresh
          </button>
        </div>
      </div>

      {updatedAt && (
        <p className="flex items-center gap-1.5 text-xs text-slate-400 mb-4">
          <ClockIcon className="w-3.5 h-3.5" />
          Updated {updatedAt.toLocaleTimeString()}
          {auditData && auditData.lastCrawl && ' | last crawl ' + String(auditData.lastCrawl).slice(0, 10)}
        </p>
      )}

      {/* action center */}
      {actions.length > 0 && (
        <div className="mb-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Do this next
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {actions.map((a, i) => {
              const t = TONE[a.tone] || TONE.indigo;
              const Icon = a.icon;
              return (
                <Link
                  key={i}
                  to={a.to}
                  className={'rounded-xl border border-slate-200 bg-white p-4 hover:shadow-md transition-shadow flex gap-3'}
                >
                  <div className={'w-9 h-9 shrink-0 rounded-lg flex items-center justify-center ' + t.bg}>
                    <Icon className={t.text} style={{ width: 18, height: 18 }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 leading-snug">{a.title}</p>
                    <p className="text-xs text-slate-500 mt-1 leading-snug">{a.body}</p>
                    <span className={'mt-2 inline-flex items-center gap-1 text-xs font-semibold ' + t.text}>
                      {a.cta} <ArrowRightIcon className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* kpi row */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
        <Kpi label="Site Health" value={health !== null && health !== undefined ? Math.round(health) : '--'} unit="/100"
             tone={scoreTone(health)} icon={ShieldExclamationIcon} to="/site-audit"
             hint={auditData ? auditData.crawledPages + ' pages crawled' : 'Run a site audit'} />
        <Kpi label="Avg Position" value={rank.avg !== null ? rank.avg : '--'}
             tone={rank.avg && rank.avg <= 10 ? 'emerald' : rank.avg && rank.avg <= 20 ? 'amber' : 'slate'}
             icon={ChartBarIcon} to="/rank-tracking"
             hint={rank.tracked + ' of ' + rank.total + ' keywords ranking'} />
        <Kpi label="Top 3" value={rank.buckets.top3} tone="emerald" icon={CheckCircleIcon} to="/rank-tracking"
             hint={rank.buckets.four10 + ' more in top 10'} />
        <Kpi label="Organic Clicks" value={google.gsc && google.gsc.current && google.gsc.current.clicks != null ? num(google.gsc.current.clicks) : '--'}
             tone="indigo" icon={CursorArrowRaysIcon} to="/integrations"
             hint={google.gsc ? 'last 28 days' : 'Google not connected'} />
        <Kpi label="AI Overview" value={rank.aiOverview} tone="indigo" icon={SparklesIcon} to="/ai-search-visibility"
             hint="keywords with AI answers" />
        <Kpi label="Critical Issues" value={auditData && auditData.summary ? auditData.summary.errors : '--'}
             tone={auditData && auditData.summary && auditData.summary.errors > 0 ? 'rose' : 'emerald'}
             icon={ExclamationTriangleIcon} to="/site-audit"
             hint={auditData && auditData.summary ? auditData.summary.warnings + ' warnings' : 'No audit yet'} />
      </div>

      {/* main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* position tracking */}
        <Card
          className="lg:col-span-2"
          title="Position Tracking"
          subtitle={domain ? 'Average rank across tracked keywords' : ''}
          action={<Link to="/rank-tracking" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">View all</Link>}
        >
          {trend.length > 1 ? (
            <div className="h-56 -ml-3">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="posGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366f1" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis reversed domain={[1, 'dataMax']} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                    formatter={(v) => ['position ' + v, '']}
                  />
                  <Area type="monotone" dataKey="position" stroke="#6366f1" strokeWidth={2} fill="url(#posGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : rank.total > 0 ? (
            <div className="h-56 flex items-center justify-center text-center">
              <div>
                <p className="text-3xl font-bold text-slate-900">{rank.avg !== null ? rank.avg : '--'}</p>
                <p className="text-xs text-slate-500 mt-1">average position today</p>
                <p className="text-xs text-slate-400 mt-3 max-w-xs">
                  History builds after a few daily checks. Refresh rankings to add a data point.
                </p>
              </div>
            </div>
          ) : (
            <Empty icon={MagnifyingGlassIcon} title="No keywords tracked yet"
                   body="Add the terms you want to win and this chart fills in automatically."
                   cta="Add keywords" to="/rank-tracking" />
          )}

          {rank.tracked > 0 && (
            <div className="grid grid-cols-5 gap-2 mt-4 pt-4 border-t border-slate-100">
              {[
                ['Top 3', rank.buckets.top3, 'emerald'],
                ['4-10', rank.buckets.four10, 'emerald'],
                ['11-20', rank.buckets.eleven20, 'amber'],
                ['21-50', rank.buckets.twentyone50, 'amber'],
                ['51+', rank.buckets.beyond, 'rose']
              ].map(([l, v, tone]) => (
                <div key={l} className="text-center">
                  <p className={'text-lg font-bold ' + TONE[tone].text}>{v}</p>
                  <p className="text-[11px] text-slate-500">{l}</p>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* site audit */}
        <Card
          title="Site Audit"
          subtitle={auditData ? auditData.totalPages + ' pages found' : 'Technical health'}
          action={<Link to="/site-audit" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">Open</Link>}
        >
          {auditData ? (
            <>
              <Gauge value={health} label="health" />
              <div className="grid grid-cols-3 gap-2 mt-4">
                {[
                  ['Errors', auditData.summary.errors, 'rose'],
                  ['Warnings', auditData.summary.warnings, 'amber'],
                  ['Passed', auditData.summary.passed, 'emerald']
                ].map(([l, v, tone]) => (
                  <div key={l} className={'rounded-lg py-2 text-center ' + TONE[tone].bg}>
                    <p className={'text-base font-bold ' + TONE[tone].text}>{v}</p>
                    <p className="text-[11px] text-slate-500">{l}</p>
                  </div>
                ))}
              </div>
              {Array.isArray(auditData.topImprovements) && auditData.topImprovements.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">Biggest wins</p>
                  <ul className="space-y-1.5">
                    {auditData.topImprovements.slice(0, 3).map((imp, i) => (
                      <li key={i} className="flex gap-2 text-xs text-slate-600">
                        <BoltIcon className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                        <span className="leading-snug">{imp.description || imp.category}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <Empty icon={ShieldExclamationIcon} title="No audit for this domain"
                   body="Crawl the site to surface errors, warnings and quick technical wins."
                   cta="Run site audit" to="/site-audit" />
          )}
        </Card>

        {/* top keywords */}
        <Card
          className="lg:col-span-2"
          title="Top Keywords"
          subtitle="Best positions, with movement since the last check"
          action={<Link to="/rank-tracking" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">View all</Link>}
        >
          {rank.top.length > 0 ? (
            <div className="overflow-x-auto -mx-5 px-5">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                    <th className="text-left font-medium pb-2">Keyword</th>
                    <th className="text-right font-medium pb-2">Pos</th>
                    <th className="text-right font-medium pb-2">Change</th>
                    <th className="text-right font-medium pb-2">Volume</th>
                    <th className="text-left font-medium pb-2 pl-4">SERP</th>
                  </tr>
                </thead>
                <tbody>
                  {rank.top.map((k) => (
                    <tr key={k.id} className="border-b border-slate-50 last:border-0">
                      <td className="py-2.5 pr-3 text-slate-700 max-w-[220px] truncate">{k.keyword}</td>
                      <td className="py-2.5 text-right font-semibold tabular-nums text-slate-900">{k.currentPosition}</td>
                      <td className={'py-2.5 text-right tabular-nums font-medium ' +
                        (k.change > 0 ? 'text-emerald-600' : k.change < 0 ? 'text-rose-600' : 'text-slate-400')}>
                        {k.change > 0 ? '+' + k.change : k.change || '0'}
                      </td>
                      <td className="py-2.5 text-right tabular-nums text-slate-500">{num(k.searchVolume)}</td>
                      <td className="py-2.5 pl-4">
                        <div className="flex gap-1 flex-wrap">
                          {(k.serpFeatures || []).slice(0, 3).map((f) => (
                            <span key={f} className={'text-[10px] px-1.5 py-0.5 rounded ' +
                              (f === 'ai_overview' ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-500')}>
                              {f === 'ai_overview' ? 'AI' : f.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty icon={MagnifyingGlassIcon} title="Nothing ranking yet"
                   body="Once keywords are tracked, your best performers appear here."
                   cta="Add keywords" to="/rank-tracking" />
          )}
        </Card>

        {/* on-page seo checker */}
        <Card
          title="On Page SEO Checker"
          subtitle="Score by audit category"
          action={<Link to="/site-audit" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">Details</Link>}
        >
          {categories ? (
            <div>
              {Object.keys(categories).slice(0, 10).map((key) => {
                const c = categories[key] || {};
                const measured = c.score !== null && c.score !== undefined && !Number.isNaN(Number(c.score));
                const s = measured ? Math.round(Number(c.score)) : 0;
                return <Bar key={key} label={key} value={s} max={100} tone={measured ? scoreTone(s) : 'slate'} right={measured ? s + '%' : 'n/a'} />;
              })}
            </div>
          ) : (
            <Empty icon={CheckCircleIcon} title="No on-page data"
                   body="Run a site audit to score content, meta, links, images and more."
                   cta="Run site audit" to="/site-audit" />
          )}
        </Card>

        {/* organic traffic / GSC */}
        <Card
          title="Organic Traffic"
          subtitle="Google Search Console, last 28 days"
          action={<Link to="/integrations" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">Settings</Link>}
        >
          {google.gsc ? (
            <div className="grid grid-cols-2 gap-3">
              {[
                ['Clicks', num(google.gsc.current && google.gsc.current.clicks)],
                ['Impressions', num(google.gsc.current && google.gsc.current.impressions)],
                ['CTR', google.gsc.current && google.gsc.current.ctr != null ? (Number(google.gsc.current.ctr) * 100).toFixed(2) + '%' : '--'],
                ['Avg position', google.gsc.current && google.gsc.current.position != null ? Number(google.gsc.current.position).toFixed(1) : '--']
              ].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-slate-50 p-3">
                  <p className="text-[11px] text-slate-500">{l}</p>
                  <p className="text-lg font-bold text-slate-900 mt-0.5">{v}</p>
                </div>
              ))}
            </div>
          ) : (
            <Empty icon={ChartBarIcon}
                   title={google.account ? 'Google data unavailable' : 'Google not connected'}
                   body={google.account
                     ? 'The saved Google account is returning an error. Re-authorise it to restore clicks and impressions.'
                     : 'Connect Search Console and Analytics to see real organic traffic here.'}
                   cta={google.account ? 'Reconnect Google' : 'Connect Google'} to="/integrations" />
          )}
        </Card>

        {/* traffic analytics / GA4 */}
        <Card
          title="Traffic Analytics"
          subtitle="Google Analytics 4"
          action={<Link to="/integrations" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">Settings</Link>}
        >
          {google.ga4 ? (
            <div className="grid grid-cols-2 gap-3">
              {[
                ['Users', num(google.ga4.current && google.ga4.current.totalUsers)],
                ['Sessions', num(google.ga4.current && google.ga4.current.sessions)],
                ['Pageviews', num(google.ga4.current && google.ga4.current.screenPageViews)],
                ['Engagement', google.ga4.current && google.ga4.current.bounceRate != null ? ((1 - Number(google.ga4.current.bounceRate)) * 100).toFixed(1) + '%' : '--']
              ].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-slate-50 p-3">
                  <p className="text-[11px] text-slate-500">{l}</p>
                  <p className="text-lg font-bold text-slate-900 mt-0.5">{v}</p>
                </div>
              ))}
            </div>
          ) : (
            <Empty icon={GlobeAltIcon} title="No analytics data"
                   body="Link a GA4 property to see users, sessions, referrals and engagement."
                   cta="Connect GA4" to="/integrations" />
          )}
        </Card>

        {/* core web vitals */}
        <Card title="Core Web Vitals" subtitle="Field and lab performance">
          {cwv ? (
            <div className="space-y-3">
              {['lcp', 'cls', 'ttfb', 'fid'].filter((k) => cwv[k]).map((k) => {
                const m = cwv[k];
                const tone = m.rating === 'good' ? 'emerald' : m.rating === 'needs-improvement' ? 'amber' : (m.rating === 'poor' ? 'rose' : 'slate');
                return (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wide text-slate-500">{m.label || k}</span>
                    <span className={'text-sm font-semibold ' + TONE[tone].text}>
                      {m.value === null || m.value === undefined || m.value === 'n/a' ? 'n/a' : m.value + (m.unit || '')}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <Empty icon={BoltIcon} title="No performance data"
                   body="Site audit collects LCP, CLS and TTFB for your key pages."
                   cta="Run site audit" to="/site-audit" />
          )}
        </Card>

        {/* backlinks + toxic */}
        <Card
          title="Backlinks and Toxic Links"
          subtitle="Referring domains and risk"
          action={<Link to="/backlink-explorer" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">Explore</Link>}
        >
          {backlinks ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                ['Referring domains', backlinks.referringDomains],
                ['Backlinks', backlinks.totalBacklinks],
                ['Domain rank', backlinks.domainRank],
                ['Spam score', backlinks.spamScore]
              ].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-slate-50 p-3">
                  <p className="text-[11px] text-slate-500">{l}</p>
                  <p className="text-lg font-bold text-slate-900 mt-0.5">{v === null || v === undefined ? '--' : v.toLocaleString()}</p>
                </div>
              ))}
              <p className="col-span-2 md:col-span-4 text-[11px] text-slate-400">DataForSEO Backlinks API{backlinks.brokenBacklinks !== null ? ' · ' + backlinks.brokenBacklinks.toLocaleString() + ' broken backlinks' : ''}</p>
            </div>
          ) : (
            <Empty icon={LinkIcon} title="No backlink audit yet"
                   body="Run a backlink audit to see referring domains, authority score and toxic links worth disavowing."
                   cta="Run backlink audit" to="/backlink-explorer" />
          )}
        </Card>

        {/* AI visibility / GEO */}
        <Card
          className="lg:col-span-2"
          title="AI Visibility and GEO"
          subtitle="Presence in ChatGPT, Gemini, Perplexity and AI Overviews"
          action={<Link to="/geo" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">Open GEO</Link>}
        >
          {aiData ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                ['Visibility score', aiData.visibilityScore],
                ['Mentions', aiData.mentions],
                ['Citations', aiData.citations],
                ['Engines', aiData.enginesCovered]
              ].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-slate-50 p-3">
                  <p className="text-[11px] text-slate-500">{l}</p>
                  <p className="text-lg font-bold text-slate-900 mt-0.5">{v === undefined || v === null ? '--' : v}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <Empty icon={SparklesIcon} title="No AI scan for this brand yet"
                     body="Find out which answers name you, which name a competitor, and which name nobody."
                     cta="Run AI Search Visibility" to="/ai-search-visibility" />
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-xs font-semibold text-slate-600 mb-2">What we already know</p>
                <p className="text-xs text-slate-500 leading-relaxed">
                  {rank.aiOverview > 0
                    ? rank.aiOverview + ' of your tracked keywords trigger an AI Overview in Google. Those are the fastest places to win an AI citation.'
                    : 'None of your tracked keywords currently trigger an AI Overview. Track more informational, question-style keywords to find AI opportunities.'}
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* activity */}
        <Card title="Recent Activity" subtitle="Scans and reports">
          <div className="space-y-3">
            {[
              ['Total scans', stats ? stats.totalScans : '--'],
              ['This month', stats ? stats.scansThisMonth : '--'],
              ['Saved reports', stats ? stats.savedReportsCount : '--'],
              ['Average score', stats ? stats.averageScore : '--']
            ].map(([l, v]) => (
              <div key={l} className="flex items-center justify-between text-sm">
                <span className="text-slate-500">{l}</span>
                <span className="font-semibold text-slate-900">{v}</span>
              </div>
            ))}
            <Link to="/my-reports"
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700">
              View reports <ArrowRightIcon className="w-3.5 h-3.5" />
            </Link>
          </div>
        </Card>

      </div>
    </div>
  );
};

export default OverviewDashboard;
